import { Circle, Group, Line, Rect, Text } from 'react-konva';
import type { DeviceKind } from '@system-atlas/shared';

interface DeviceIconProps {
  kind: DeviceKind;
  /** Kantenlaenge der quadratischen Zeichenflaeche in Pixel. */
  size: number;
  color: string;
  x?: number;
  y?: number;
}

/**
 * Einheitliche, einfarbige Strich-Icons je Geraetetyp, komponiert aus Konva-Primitiven.
 * Alle Koordinaten sind Bruchteile von `size`, damit das Icon mit dem Knoten skaliert.
 */
export function DeviceIcon({ kind, size: s, color, x = 0, y = 0 }: DeviceIconProps) {
  const sw = Math.max(1.5, s * 0.05);
  const stroke = { stroke: color, strokeWidth: sw, lineCap: 'round' as const, lineJoin: 'round' as const };
  const dot = { fill: color, radius: s * 0.025 };

  const shapes = (() => {
    switch (kind) {
      case 'pc':
        return (
          <>
            <Rect x={0.12 * s} y={0.18 * s} width={0.76 * s} height={0.46 * s} cornerRadius={0.04 * s} {...stroke} />
            <Line points={[0.5 * s, 0.64 * s, 0.5 * s, 0.78 * s]} {...stroke} />
            <Line points={[0.34 * s, 0.8 * s, 0.66 * s, 0.8 * s]} {...stroke} />
          </>
        );
      case 'laptop':
        return (
          <>
            <Rect x={0.22 * s} y={0.2 * s} width={0.56 * s} height={0.36 * s} cornerRadius={0.03 * s} {...stroke} />
            <Line points={[0.22 * s, 0.56 * s, 0.16 * s, 0.66 * s]} {...stroke} />
            <Line points={[0.78 * s, 0.56 * s, 0.84 * s, 0.66 * s]} {...stroke} />
            <Line points={[0.16 * s, 0.66 * s, 0.84 * s, 0.66 * s]} {...stroke} />
          </>
        );
      case 'printer':
        return (
          <>
            <Rect x={0.3 * s} y={0.22 * s} width={0.4 * s} height={0.16 * s} {...stroke} />
            <Rect x={0.16 * s} y={0.38 * s} width={0.68 * s} height={0.3 * s} cornerRadius={0.03 * s} {...stroke} />
            <Rect x={0.32 * s} y={0.6 * s} width={0.36 * s} height={0.14 * s} {...stroke} />
            <Circle x={0.72 * s} y={0.46 * s} {...dot} />
          </>
        );
      case 'scanner':
        return (
          <>
            <Rect x={0.16 * s} y={0.34 * s} width={0.68 * s} height={0.12 * s} cornerRadius={0.02 * s} {...stroke} />
            <Rect x={0.16 * s} y={0.48 * s} width={0.68 * s} height={0.2 * s} cornerRadius={0.02 * s} {...stroke} />
            <Circle x={0.74 * s} y={0.58 * s} {...dot} />
          </>
        );
      case 'phone':
        return (
          <>
            <Rect x={0.34 * s} y={0.14 * s} width={0.32 * s} height={0.72 * s} cornerRadius={0.07 * s} {...stroke} />
            <Line points={[0.45 * s, 0.22 * s, 0.55 * s, 0.22 * s]} {...stroke} />
            <Circle x={0.5 * s} y={0.79 * s} {...dot} />
          </>
        );
      case 'accesspoint':
        return (
          <>
            <Rect x={0.22 * s} y={0.5 * s} width={0.56 * s} height={0.18 * s} cornerRadius={0.04 * s} {...stroke} />
            <Circle x={0.5 * s} y={0.59 * s} {...dot} />
            <Line points={[0.34 * s, 0.5 * s, 0.3 * s, 0.34 * s]} {...stroke} />
            <Line points={[0.66 * s, 0.5 * s, 0.7 * s, 0.34 * s]} {...stroke} />
          </>
        );
      case 'camera':
        return (
          <>
            <Rect x={0.3 * s} y={0.24 * s} width={0.18 * s} height={0.08 * s} {...stroke} />
            <Rect x={0.16 * s} y={0.32 * s} width={0.68 * s} height={0.4 * s} cornerRadius={0.04 * s} {...stroke} />
            <Circle x={0.5 * s} y={0.52 * s} radius={0.12 * s} {...stroke} />
            <Circle x={0.5 * s} y={0.52 * s} fill={color} radius={0.04 * s} />
          </>
        );
      case 'server':
        return (
          <>
            <Rect x={0.26 * s} y={0.14 * s} width={0.48 * s} height={0.72 * s} cornerRadius={0.03 * s} {...stroke} />
            <Line points={[0.34 * s, 0.3 * s, 0.66 * s, 0.3 * s]} {...stroke} />
            <Line points={[0.34 * s, 0.46 * s, 0.66 * s, 0.46 * s]} {...stroke} />
            <Line points={[0.34 * s, 0.62 * s, 0.66 * s, 0.62 * s]} {...stroke} />
            <Circle x={0.34 * s} y={0.76 * s} {...dot} />
            <Circle x={0.42 * s} y={0.76 * s} {...dot} />
          </>
        );
      case 'pbx':
        return (
          <>
            <Rect x={0.18 * s} y={0.28 * s} width={0.64 * s} height={0.5 * s} cornerRadius={0.03 * s} {...stroke} />
            {[0.42, 0.58].map((cy) =>
              [0.34, 0.5, 0.66].map((cx) => (
                <Circle key={`${cx}-${cy}`} x={cx * s} y={cy * s} {...dot} />
              )),
            )}
          </>
        );
      case 'deskphone':
        return (
          <>
            <Rect x={0.18 * s} y={0.5 * s} width={0.64 * s} height={0.28 * s} cornerRadius={0.04 * s} {...stroke} />
            <Line points={[0.24 * s, 0.4 * s, 0.76 * s, 0.4 * s]} {...stroke} strokeWidth={sw * 1.4} />
            <Line points={[0.24 * s, 0.4 * s, 0.22 * s, 0.47 * s]} {...stroke} />
            <Line points={[0.76 * s, 0.4 * s, 0.78 * s, 0.47 * s]} {...stroke} />
            {[0.4, 0.5, 0.6].map((cx) => (
              <Circle key={cx} x={cx * s} y={0.63 * s} {...dot} />
            ))}
          </>
        );
      case 'other':
      default:
        return (
          <>
            <Rect x={0.2 * s} y={0.2 * s} width={0.6 * s} height={0.6 * s} cornerRadius={0.06 * s} {...stroke} />
            <Text
              text="?"
              x={0.2 * s}
              y={0.27 * s}
              width={0.6 * s}
              align="center"
              fontStyle="bold"
              fontSize={0.4 * s}
              fill={color}
            />
          </>
        );
    }
  })();

  return (
    <Group x={x} y={y} listening={false}>
      {shapes}
    </Group>
  );
}
